import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Language, translations } from "./translations";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const supportedLanguages = Object.keys(translations) as Language[];
const LANGUAGE_STORAGE_KEY = "preferredLanguage";
const LANGUAGE_STORAGE_EXPLICIT_KEY = "preferredLanguageExplicit";

const isSupportedLanguage = (lang: string): lang is Language =>
  supportedLanguages.includes(lang as Language);

const normalizeBrowserLanguage = (locale: string) =>
  locale.toLowerCase().split("-")[0];

const getStoredLanguage = (): Language | null => {
  if (typeof window === "undefined") return null;
  if (window.localStorage.getItem(LANGUAGE_STORAGE_EXPLICIT_KEY) !== "true") return null;

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return storedLanguage && isSupportedLanguage(storedLanguage) ? storedLanguage : null;
};

const storeExplicitLanguage = (lang: Language) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  window.localStorage.setItem(LANGUAGE_STORAGE_EXPLICIT_KEY, "true");
};

const detectSystemLanguage = (): Language => {
  if (typeof navigator === "undefined") return "en";

  const browserLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const locale of browserLanguages) {
    const lang = normalizeBrowserLanguage(locale);
    if (isSupportedLanguage(lang)) return lang;
  }

  return "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getUrlLanguage = useCallback((): Language | null => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    return pathParts.length > 0 && isSupportedLanguage(pathParts[0]) ? pathParts[0] : null;
  }, [location.pathname]);

  // Prefer language from URL, then an explicitly selected saved language, otherwise detect the user's system language.
  const getInitialLang = (): Language => getUrlLanguage() ?? getStoredLanguage() ?? detectSystemLanguage();

  const [language, setLanguageState] = useState<Language>(getInitialLang);

  useEffect(() => {
    const urlLanguage = getUrlLanguage();
    if (urlLanguage && urlLanguage !== language) {
      setLanguageState(urlLanguage);
    }
  }, [getUrlLanguage, language]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
      document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    storeExplicitLanguage(lang);
    setLanguageState(lang);
    const pathParts = location.pathname.split("/").filter(Boolean);

    if (pathParts.length > 0 && isSupportedLanguage(pathParts[0])) {
      pathParts.shift();
    }

    if (lang === "en") {
      navigate("/" + pathParts.join("/"), { replace: true });
    } else {
      navigate("/" + lang + (pathParts.length ? "/" + pathParts.join("/") : ""), { replace: true });
    }
  }, [navigate, location.pathname]);

  const t = useCallback(
    (key: string) => translations[language]?.[key] || translations.en[key] || key,
    [language]
  );

  const isRTL = language === "ar";

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRTL }}>
      <div dir={isRTL ? "rtl" : "ltr"}>{children}</div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
